/** Normalize exam / quiz JSON records into a single reader view-model. */

import { markdownTablesToHtml } from '../../quiz-generation/utils/markdownTablesToHtml';
import { sanitizeTsaProseMath } from '../../quiz-generation/utils/sanitizeTsaProseMath';
import { sanitizeHtml } from '../../../lib/sanitizeHtml';

const OPTION_ORDER = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

export function stripHtml(html) {
  if (!html) return '';
  // DOMParser, not a detached div: <img onerror> fires even in a detached div.
  const doc = new DOMParser().parseFromString(String(html), 'text/html');
  return (doc.body.textContent || '').trim();
}

function asHtml(text, preferHtml) {
  if (text == null || text === '') return '';
  const s = markdownTablesToHtml(sanitizeTsaProseMath(String(text)));
  if (preferHtml !== false && s.trim().startsWith('<')) return sanitizeHtml(s);
  // Prose + converted table: keep breaks, don't wrap the whole stem in one <p>
  // (invalid to nest <table> inside <p>).
  if (s.includes('<table')) return sanitizeHtml(s.replace(/\n/g, '<br/>'));
  if (s.includes('$') || s.includes('$$')) return sanitizeHtml(s);
  return sanitizeHtml(`<p>${s.replace(/\n/g, '<br/>')}</p>`);
}

function sortedByLetter(options) {
  return [...options].sort(
    (a, b) => OPTION_ORDER.indexOf(a.letter) - OPTION_ORDER.indexOf(b.letter),
  );
}

function collectOptions(q) {
  const lists = [];

  if (Array.isArray(q.answers) && q.answers.length) {
    lists.push(
      q.answers.map((a, i) => ({
        letter: String(a.answer_letter || OPTION_ORDER[i] || '').toUpperCase(),
        html: asHtml(a.answer_text, a.is_latex !== false || q.is_latex),
        isCorrect: Boolean(a.is_correct),
      })),
    );
  }

  if (Array.isArray(q.options) && q.options.length) {
    const correct = String(q.correct_label || q.correct_answer || '').toUpperCase();
    lists.push(
      q.options.map((o, i) => {
        const letter = String(o.label || o.answer_letter || OPTION_ORDER[i] || '').toUpperCase();
        const raw = o.text ?? o.answer_text ?? o.html ?? '';
        return {
          letter,
          html: asHtml(raw, true),
          isCorrect: Boolean(o.is_correct) || (correct && letter === correct),
        };
      }),
    );
  }

  const best = lists.find((l) => l.length > 0) || [];
  return sortedByLetter(best.filter((o) => o.letter));
}

function detectKind(q, options) {
  const typeHint = String(
    q.question_type || q.format || q.question_format || q.response_type || '',
  ).toLowerCase();

  if (typeHint.includes('essay')) return 'essay_choice';
  if (q.essay_choice === true) return 'essay_choice';

  if (q.multiple_choice === false || q.is_text_answer === true) {
    if (options.length >= 2) {
      const avgLen = options.reduce((sum, o) => sum + stripHtml(o.html).length, 0) / options.length;
      if (avgLen > 60 || typeHint.includes('choice')) return 'essay_choice';
    }
    return 'text_response';
  }

  if (!options.length) return 'text_response';
  return 'multiple_choice';
}

function stemHtml(q) {
  const raw = q.question_text ?? q.question ?? q.stem ?? '';
  return asHtml(raw, q.exhibit_is_html !== false || String(raw).trim().startsWith('<'));
}

function exhibitBlock(q) {
  if (q.requires_exhibit === false || !q.exhibit_text) return { show: false, html: '' };
  const raw = sanitizeTsaProseMath(String(q.exhibit_text));
  return {
    show: true,
    html: sanitizeHtml(q.exhibit_is_html !== false ? raw : `<p>${stripHtml(raw)}</p>`),
  };
}

export function normalizeExamQuestion(q, index = 0) {
  if (!q || typeof q !== 'object') return null;

  const options = collectOptions(q);
  const kind = detectKind(q, options);
  const correct = options.find((o) => o.isCorrect);

  return {
    id: q.external_question_id || q.question_id || q.id || `Q${index + 1}`,
    kind,
    badge:
      kind === 'essay_choice'
        ? 'Essay choice'
        : kind === 'text_response'
          ? 'Text response'
          : null,
    exhibit: exhibitBlock(q),
    stemHtml: stemHtml(q),
    sectionHtml: sanitizeHtml(q.section_instructions || q.section_html || ''),
    options,
    correctLetter: correct?.letter || String(q.correct_answer || q.correct_label || '').toUpperCase(),
    explanationHtml: asHtml(
      q.explanation || q.explanation_html || q.model_answer || '',
      true,
    ),
    workingSteps: Array.isArray(q.working_steps)
      ? q.working_steps.map((s) => asHtml(s, true))
      : [],
    wordLimit: q.word_limit || q.max_words || 600,
    placeholder:
      q.text_placeholder
      || 'Write your response to the question you selected.',
  };
}

export function normalizeExamQuestions(source) {
  const raw = Array.isArray(source)
    ? source
    : (source?.questions || []);
  return raw
    .map((q, i) => normalizeExamQuestion(q, i))
    .filter(Boolean);
}

export function waitForMathJax() {
  return new Promise((resolve) => {
    if (window.MathJax?.startup?.promise) {
      window.MathJax.startup.promise.then(resolve).catch(() => resolve());
      return;
    }
    if (window.MathJax?.typesetPromise) {
      resolve();
      return;
    }
    let waited = 0;
    const tick = setInterval(() => {
      waited += 100;
      if (window.MathJax?.typesetPromise || waited >= 10000) {
        clearInterval(tick);
        if (window.MathJax?.startup?.promise) {
          window.MathJax.startup.promise.then(resolve).catch(() => resolve());
        } else {
          resolve();
        }
      }
    }, 100);
  });
}

export async function typesetMathJax(rootEl) {
  if (!rootEl || !window.MathJax?.typesetPromise || !rootEl.isConnected) return;
  await waitForMathJax();
  try {
    const nodes = rootEl.querySelectorAll('.mathjax-process');
    const targets = Array.from(nodes).filter((el) => {
      if (!el || !el.isConnected) return false;
      const html = el.innerHTML || '';
      return (html.includes('$') || html.includes('\\')) && !el.querySelector('.MathJax');
    });
    const validTargets = targets.filter(el => el && el.isConnected && el.parentNode);
    if (validTargets.length > 0) {
      await window.MathJax.typesetPromise(validTargets);
    }
  } catch {
    if (window.MathJax?.typesetClear) {
      try { window.MathJax.typesetClear(); } catch (e) {}
    }
  }
}
